import { useState } from 'react'
import LeafletMap from '../specific/LeafletMap'
import MapSheet from '../specific/MapSheet'
import Button from '../common/Button'
import { inputClass } from '../common/Field'
import { useLocationSearch } from '../../hooks/useLocationSearch'
import { categoryOf, isOpenNow, hoursLabel } from '../../data/stores'

/**
 * One box to answer *where*.
 *
 * It began as four ways stacked down the screen — the shop list, the places this browser
 * had used, current location, then search, then a link box under that. Every one of them
 * defensible on its own, and together a screen the customer has to *survey* before they
 * can answer a question. On a step whose entire premise is having one thing on it, that
 * was the wrong shape.
 *
 * So: **type, and the right answer appears.** The shop list did not leave the product, it
 * moved behind the search box and is consulted first — see `useLocationSearch`. Pasting a
 * map link moved into the full map, where somebody who wants it will go looking. Saved
 * places went entirely.
 *
 * Two buttons survive beside the box, because neither is something a customer can type:
 * their own position, and a pin they place by eye.
 *
 * ## Saved places came back, and not as a section
 *
 * The sentence above is why they left, and it still holds: the problem was never the data,
 * it was four things stacked down a screen whose whole premise is having one thing on it.
 * So the data came back and the section did not.
 *
 * **The dropoff arrives already answered.** A house does not move, so for a returning
 * customer the hardest question on the form is seeded from their last order and *Sunod* is
 * the only thing left to press — see `payload.js`. Nothing is added to this screen for that
 * to happen.
 *
 * **The pickup arrives as rows inside the box that is already here.** The shop changes from
 * order to order, so seeding it would be wrong more often than right; instead the remembered
 * shops answer the empty box, in the same list the shop search already fills. Still one
 * control, still one question.
 *
 * The dropoff gets those rows too, and only for one reason: typing releases the pin
 * (`useLocationSearch`), so a customer who touches their seeded answer needs one tap back to
 * it rather than a search for their own house.
 */

const PICKUP_HINT = 'Hal. Jollibee, o SM Calamba'
const DROPOFF_HINT = 'Hal. UP Los Baños'

/**
 * Read on demand rather than on mount.
 *
 * The existing `useGeolocation` asks the moment it renders, which throws the browser's
 * permission prompt at a customer who has not indicated they want it — and a prompt
 * dismissed once is awkward to get back.
 */
function useCurrentPlace(onFound) {
  const [state, setState] = useState({ busy: false, error: null })

  const ask = () => {
    if (!navigator.geolocation) {
      setState({ busy: false, error: 'Hindi available ang lokasyon sa browser na ito.' })
      return
    }

    setState({ busy: true, error: null })

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setState({ busy: false, error: null })
        onFound({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          // No label: the pin is the answer, and inventing a street name for it would put
          // a guess in front of the rider.
          label: '',
        })
      },
      () => setState({ busy: false, error: 'Hindi makuha ang lokasyon mo. Maghanap na lang o mag-drop ng pin sa mapa.' }),
      { timeout: 8000 }
    )
  }

  return { ...state, ask }
}

/**
 * Two remembered rows are the same place when their pins agree to about eleven metres.
 *
 * The same rule `places.js` dedupes on, and for the same reason: the label is the thing
 * being edited, so it cannot be what identifies the row.
 */
const samePlace = (a, b) => (
  Boolean(a) && Boolean(b)
  && Math.round(a.lat * 1e4) === Math.round(b.lat * 1e4)
  && Math.round(a.lng * 1e4) === Math.round(b.lng * 1e4)
)

/**
 * The row, turned into the one question it is asking: what do you call this?
 *
 * Replaces the row rather than growing underneath it. A dropdown that changes height under
 * a thumb is a dropdown that gets mis-tapped, and this list is read by somebody holding a
 * cheap phone in one hand.
 */
function RenameRow({ place, kind, onSave, onCancel }) {
  const [name, setName] = useState(place.label ?? '')

  return (
    <div className="px-3.5 py-2.5 bg-surface-2">
      <label htmlFor={`${kind}-rename`} className="block text-label font-semibold uppercase text-ink-2">
        Ano ang tawag dito?
      </label>

      <input
        id={`${kind}-rename`}
        type="text"
        autoFocus
        value={name}
        placeholder={kind === 'pickup' ? 'Hal. Jollibee' : 'Hal. bahay ni Nanay'}
        onChange={(event) => setName(event.target.value)}
        onKeyDown={(event) => {
          // Enter saves and Escape backs out, because this sits inside a form and a stray
          // Enter would otherwise submit the step.
          if (event.key === 'Enter') { event.preventDefault(); onSave(name) }
          if (event.key === 'Escape') { event.preventDefault(); onCancel() }
        }}
        className={`${inputClass(false)} mt-1.5`}
      />

      <div className="flex gap-2.5 mt-2.5">
        <Button type="button" variant="ghost" size="compact" onClick={onCancel} className="flex-1">
          Huwag na
        </Button>

        <Button type="button" size="compact" onClick={() => onSave(name)} className="flex-1">
          I-save
        </Button>
      </div>
    </div>
  )
}

/**
 * Asked before it is done, and asked in the row itself.
 *
 * Forgetting is small and it is not undoable — the place comes back only by being used on
 * another order. A confirm is cheap here and a mis-tap is not: these two controls sit on a
 * row whose whole purpose is to be tapped.
 */
function ForgetRow({ label, onForget, onCancel }) {
  return (
    <div className="px-3.5 py-2.5 bg-surface-2">
      <p className="text-body-s text-ink">
        Kalimutan ang <strong>{label}</strong>?
      </p>

      <div className="flex gap-2.5 mt-2.5">
        <Button type="button" variant="ghost" size="compact" onClick={onCancel} className="flex-1">
          Huwag na
        </Button>

        <Button type="button" variant="destructive" size="compact" onClick={onForget} className="flex-1">
          Kalimutan
        </Button>
      </div>
    </div>
  )
}

export default function PlacePicker({ kind, loc, setLoc, error, errorMessage, onPickName }) {
  const {
    suggestions, handleQueryChange, selectSuggestion, showRemembered,
    renameRemembered, forgetRemembered,
  } = useLocationSearch(loc, setLoc, kind)

  /**
   * One remembered row at a time, and never both at once.
   *
   * Held here rather than inside the row so opening a second closes the first: two open
   * editors in a five-row dropdown is the surveying problem again, in miniature.
   */
  const [editing, setEditing] = useState(null)
  const [forgetting, setForgetting] = useState(null)

  const [sheetOpen, setSheetOpen] = useState(false)
  const current = useCurrentPlace(selectSuggestion)

  const searchId = `${kind}-search`
  const placed = loc.lat != null && loc.lng != null

  /**
   * One tap sets the pin *and* names the place.
   *
   * The pickup step needs both — the pin is where the rider rides, the name is what they
   * ask for on arrival — and making somebody type the second after choosing the first is
   * the two-boxes-for-one-question problem this redesign exists to remove.
   *
   * A listed shop is already just its name. Nominatim returns the whole address in one
   * string, so its first comma-segment is taken: the establishment where there is one, the
   * street where there is not.
   */
  const choose = (suggestion) => {
    selectSuggestion(suggestion)

    // A remembered place carries the name the customer actually sent last time, which beats
    // deriving one: it is what they called the shop, not what the address happens to start
    // with.
    const name = suggestion.name
      ? suggestion.name
      : suggestion.store
        ? suggestion.label
        : String(suggestion.label ?? '').split(',')[0].trim()

    if (name) onPickName?.(name)
  }

  return (
    <div className="grid gap-3">
      <div>
        <label htmlFor={searchId} className="block text-label font-semibold uppercase text-ink-2 mb-1.5">
          Hanapin
        </label>

        <div className="relative flex items-center">
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
            className="absolute left-3 w-4 h-4 text-ink-3 pointer-events-none"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>

          <input
            id={searchId}
            type="text"
            autoComplete="off"
            placeholder={kind === 'pickup' ? PICKUP_HINT : DROPOFF_HINT}
            value={loc.query}
            onChange={(event) => handleQueryChange(event.target.value)}
            /* So the remembered places are reachable without typing a character first —
               which is the whole point of them for somebody who finds typing hard. */
            onFocus={showRemembered}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? `${kind}-error` : undefined}
            className={`${inputClass(error)} pl-9`}
          />
        </div>

        {suggestions !== null && (
          <ul className="relative z-[5] -mt-px max-h-[200px] overflow-auto rounded-b-chip border border-t-0 border-edge bg-surface shadow-sheet">
            {suggestions === 'error' && (
              <li className="px-3.5 py-2 text-body-s text-ink-3">
                Hindi gumagana ang paghahanap ngayon. Mag-drop na lang ng pin sa mapa.
              </li>
            )}

            {/**
              * "Walang nahanap" on its own is a dead end, and it is the most common one
              * here — no geocoder holds a house number in a sitio. *9157 Sitio Pagkakaisa*
              * is a real address that Google and OpenStreetMap alike will answer with
              * nothing, because nobody has ever recorded it.
              *
              * The pin is the answer to that, and always was. So the empty state stops
              * being a report and becomes the instruction: drop it yourself, on the map
              * below.
              */}
            {suggestions !== 'error' && suggestions.length === 0 && (
              <li className="px-3.5 py-3">
                <p className="text-body-s text-ink">Walang nahanap na lugar na ganyan.</p>
                <button
                  type="button"
                  onClick={() => setSheetOpen(true)}
                  className="inline-flex items-center min-h-[36px] mt-1 text-body-s font-semibold text-brand
                             transition-colors duration-quick ease-out hover:text-brand-lift"
                >
                  Ituro na lang sa mapa →
                </button>
                <p className="mt-0.5 text-label text-ink-3">
                  Madalas walang tala ang eksaktong bahay o sitio. Ang pin ang pinakatiyak.
                </p>
              </li>
            )}

            {suggestions !== 'error' &&
              suggestions.map((suggestion, index) => (
                <li key={index} className="border-b border-hairline last:border-b-0">
                  {/* The row becomes the form, rather than growing one underneath it. A
                      dropdown that changes height under a thumb is a dropdown that gets
                      mis-tapped. */}
                  {suggestion.remembered && samePlace(editing, suggestion.place) ? (
                    <RenameRow
                      place={suggestion.place}
                      kind={kind}
                      onSave={(label) => { renameRemembered(suggestion.place, label); setEditing(null) }}
                      onCancel={() => setEditing(null)}
                    />
                  ) : suggestion.remembered && samePlace(forgetting, suggestion.place) ? (
                    <ForgetRow
                      label={suggestion.label}
                      onForget={() => { forgetRemembered(suggestion.place); setForgetting(null) }}
                      onCancel={() => setForgetting(null)}
                    />
                  ) : (
                  <div className="flex items-stretch">
                  <button
                    type="button"
                    onClick={() => choose(suggestion)}
                    className="block flex-1 min-w-0 min-h-[44px] px-3.5 py-2 text-left
                               transition-colors duration-quick ease-out hover:bg-blush"
                  >
                    <span className="block text-body-s text-ink truncate">
                      {suggestion.label}
                    </span>

                    {/* Where it is, under what it is called. Four Jollibees share a name
                        in this town and three of them share a street, so the town goes on
                        the line too — it is the only thing telling the last pair apart. */}
                    {suggestion.secondary && (
                      <span className="block text-label text-ink-3 truncate">
                        {suggestion.secondary}
                      </span>
                    )}

                    {/* What kind of shop, and whether it is trading. Only a listed shop
                        carries either — Nominatim returns an address and nothing about the
                        business at it. */}
                    {suggestion.store && (
                      <span className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-label uppercase text-brand">
                          {categoryOf(suggestion.store).label}
                        </span>

                        {isOpenNow(suggestion.store) === false && (
                          <span className="text-label uppercase text-alert">· Sarado</span>
                        )}

                        {isOpenNow(suggestion.store) === true && hoursLabel(suggestion.store) && (
                          <span className="text-label uppercase text-ink-3 tabular-nums">
                            · {hoursLabel(suggestion.store)}
                          </span>
                        )}
                      </span>
                    )}
                  </button>

                  {/**
                    * Only a remembered place carries these.
                    *
                    * A shop row and a Nominatim row are somebody else's data — there is
                    * nothing on them this customer owns or could correct. These two rows
                    * are theirs: the label is what the rider is read out at the gate, and
                    * the entry itself is one they can outgrow by moving house.
                    *
                    * Icons rather than words, on the row rather than under it, so the list
                    * keeps its shape — the alternative is a third line per row and a
                    * dropdown that has to be *read* before it can be tapped.
                    */}
                  {suggestion.remembered && (
                    <span className="flex-none flex items-center pr-1.5">
                      <button
                        type="button"
                        onClick={() => setEditing(suggestion.place)}
                        aria-label={`Palitan ang pangalan ng ${suggestion.label}`}
                        className="w-11 h-11 grid place-items-center rounded-chip text-ink-3
                                   transition-colors duration-quick ease-out hover:text-brand"
                      >
                        <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none"
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                          strokeLinejoin="round" aria-hidden="true"
                        >
                          <path d="M12 20h9" />
                          <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                        </svg>
                      </button>

                      <button
                        type="button"
                        onClick={() => setForgetting(suggestion.place)}
                        aria-label={`Kalimutan ang ${suggestion.label}`}
                        className="w-11 h-11 grid place-items-center rounded-chip text-ink-3
                                   transition-colors duration-quick ease-out hover:text-alert"
                      >
                        <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none"
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                          strokeLinejoin="round" aria-hidden="true"
                        >
                          <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
                        </svg>
                      </button>
                    </span>
                  )}
                  </div>
                  )}
                </li>
              ))}
          </ul>
        )}
      </div>

      <Button
        variant="ghost"
        size="compact"
        onClick={current.ask}
        disabled={current.busy}
        className="w-full"
      >
        {current.busy ? 'Hinahanap…' : 'Gamitin ang lokasyon ko ngayon'}
      </Button>

      {current.error && (
        <p role="alert" className="text-body-s text-alert">
          {current.error}
        </p>
      )}

      {/* A still picture of the answer. Panning and zooming are off, so a thumb scrolling
          the form never gets caught by the map, and moving the pin happens on the full
          screen where there is room to do it — and where pasting a map link now lives. */}
      <div className="rounded-card overflow-hidden border border-hairline">
        <LeafletMap lat={loc.lat} lng={loc.lng} variant={kind} className="h-[180px] w-full" />
      </div>

      <Button variant="ghost" size="compact" onClick={() => setSheetOpen(true)} className="w-full">
        {placed ? 'Buksan ang mapa para ilipat ang pin' : 'Buksan ang mapa para mag-drop ng pin'}
      </Button>

      {/**
        * What this place is called, and the customer gets to say.
        *
        * This line used to be a read-only caption that fell back to `14.16942, 121.24191`
        * whenever the pin was dropped by hand rather than searched for — which is exactly
        * the case where the customer has the most to say and the app knew the least. Six
        * decimal places is not an address to anybody, it is not what the rider wants read
        * out on the phone, and it is what the partner's screen showed for the delivery.
        *
        * So it is an input. Searched places arrive with their name already in it and
        * nobody has to touch it; a hand-dropped pin arrives empty and asks. The pin is
        * never affected — moving the map changes where, this changes what it is called,
        * and the two are separate questions that were sharing one field.
        */}
      {/**
        * The pickup does not get this box, because it already has one.
        *
        * `PickupStep` asks *"Pangalan ng tindahan o lugar"* directly under this picker, and
        * that is the same question — it is the field the rider is told to ask for, and
        * choosing a search result fills it in the same tap that sets the pin. Adding *"Ano
        * ang tawag dito?"* above it put two boxes on screen wanting one answer, which is
        * the exact shape the comment at the top of this file was written to prevent.
        *
        * The dropoff has no such field. Its label is the whole of what the partner's screen
        * and the rider's phone are given, so there it is the only place the customer can
        * say what their own house is called.
        */}
      {placed && kind === 'dropoff' ? (
        <div>
          <label
            htmlFor={`${kind}-label`}
            className="block text-label font-semibold uppercase text-ink-2 mb-1.5"
          >
            Ano ang tawag dito?
          </label>

          <input
            id={`${kind}-label`}
            type="text"
            autoComplete="off"
            value={loc.label}
            placeholder={kind === 'pickup' ? 'Hal. Jollibee' : 'Hal. bahay ni Nanay'}
            onChange={(event) => setLoc((prev) => ({ ...prev, label: event.target.value }))}
            className={inputClass(false)}
          />

          <p className="mt-1.5 text-label text-ink-3">
            Ito po ang makikita ng rider. Ang pin sa mapa ang nagsasabi kung saan.
          </p>
        </div>
      ) : !placed ? (
        <p className="text-body-s text-ink-3">Wala pang napipiling lugar.</p>
      ) : null}

      {error && (
        <p id={`${kind}-error`} role="alert" className="text-body-s text-alert">
          {errorMessage}
        </p>
      )}

      {sheetOpen && (
        <MapSheet
          kind={kind}
          title={kind === 'pickup' ? 'Saan kukuha' : 'Saan ihahatid'}
          loc={loc}
          setLoc={setLoc}
          onClose={() => setSheetOpen(false)}
        />
      )}
    </div>
  )
}
